import { ReportJWTResp } from '@monetize/types/app';
import { METABASE_CONFIG } from '@monetize/ui/config';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { doGetReportingJWT } from '../../api/reportingService';
import { useToast } from '../../services/toast';
import { MBox, MSpinner } from '../Monetize';

const RETURN_TO_PARAM = 'reportingReturnTo';

export const EmbeddedMetabase = () => {
  const [metabaseJWT, setMetabaseJWT] = useState<ReportJWTResp | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { current: metabaseSearchParams } = useRef(
    new URLSearchParams(METABASE_CONFIG.appearance),
  );
  const { addToast } = useToast();
  const navigate = useNavigate();

  /**
   * Listen for URL messages from Metabase so we can store the reporting URL
   * in the parent application to allow deep-linking back to metabase.
   */
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the message is from Metabase
      if (event.origin !== new URL(METABASE_CONFIG.baseUrl).origin) {
        return;
      }

      // Check if it's a Metabase location message
      if (event.data?.metabase?.type === 'location') {
        const { location } = event.data.metabase;

        let reportingPath = '';
        if (typeof location === 'string') {
          // If location is a URL string
          try {
            const url = new URL(location);
            reportingPath = url.pathname + url.search;
          } catch {
            reportingPath = location;
          }
        } else if (location?.pathname) {
          reportingPath = `${location.pathname}${location.search || ''}`;
        }

        if (reportingPath) {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set(
            RETURN_TO_PARAM,
            encodeURIComponent(reportingPath),
          );
          setSearchParams(newSearchParams, { replace: true });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [searchParams, setSearchParams]);

  // Fetch JWT SSO token
  useEffect(() => {
    (async () => {
      try {
        const jwtResp = await doGetReportingJWT();
        metabaseSearchParams.set('jwt', jwtResp.jwt);
        const reportingUrl = searchParams.get(RETURN_TO_PARAM);
        if (reportingUrl) {
          metabaseSearchParams.set(
            'return_to',
            decodeURIComponent(reportingUrl),
          );
        }
        setMetabaseJWT(jwtResp);
      } catch (err) {
        addToast({
          summary: 'Error',
          detail: 'Failed to fetch credentials. Please try again later.',
          severity: 'error',
        });
        navigate(-1);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!metabaseJWT || !metabaseJWT.jwt) {
    return (
      <MBox
        height="50vh"
        justifyContent="center"
        display="flex"
        alignItems="center"
      >
        <MSpinner />
      </MBox>
    );
  }

  return (
    <MBox height="100%" flex="1" display="flex" flexDirection="column">
      <iframe
        src={`${METABASE_CONFIG.embeddedUrl}?${metabaseSearchParams.toString()}`}
        style={{ width: '100%', height: '100%', flex: 1 }}
      />
    </MBox>
  );
};
